import { useState, useEffect } from 'react';
import { supabase, DateUtils, getStatusColor, getJobStatusLabel } from '@cleanpro/common-ui';
import type { JobWithRelations, JobStatus, PropertyType, ServiceCategory } from '@cleanpro/common-ui';
import { useParams, Link } from 'react-router-dom';
import { useAuth } from '@clerk/clerk-react';
import { useBusinesses } from '../hooks/useBusinesses';
import { ArrowLeft, Building2, Mail, Phone, MapPin, Calendar, Shield, User, Users, Plus, X, Edit, Save, Briefcase as JobIcon, Briefcase, ExternalLink, CreditCard, Globe, CheckCircle2, XCircle, AlertCircle } from 'lucide-react';
import { Business } from '../business';
import BusinessServicesSection from './BusinessServicesSection';

interface ClerkSubscription {
  id: string;
  status: 'active' | 'inactive';
  createdAt?: number;
  plan: {
    id: string;
    name: string;
    period: 'month' | 'year';
    price?: number;
  } | null;
  nextPayment: {
    amount: number;
    currency: string;
    date: number;
  } | null;
  periodEnd: number | null;
  isFreeTrial: boolean;
  paymentMethod?: {
    type: string;
    last4: string;
  } | null;
  lifetimePaid?: number;
  billingCycle?: 'monthly' | 'annual';
}

interface ClerkOrganizationMember {
  id: string;
  role: string;
  public_user_data: {
    first_name: string;
    last_name: string;
    identifier: string;
    has_image: boolean;
    image_url?: string;
    profile_image_url?: string;
    last_sign_in_at?: string;
    two_factor_enabled: boolean;
    banned: boolean;
    locked: boolean;
  };
}

interface BroadcastedQuote {
  id: string;
  quote_number: string;
  requested_date_time: string | null;
  quote_type: 'exclusive' | 'broadcast';
  broadcast_status: string | null;
  status: string;
  business_id: string | null;
  total_amount: number | null;
  valid_until: string | null;
  responded_at: string | null;
  assigned_business_id: string | null;
  assigned_business_name: string | null;
}

interface QuoteFromRPC {
  broadcast_id: string;
  business_id: string;
  created_at: string;
  customer_id: string;
  id: string;
  is_exclusive: boolean;
  organization_id: string;
  property_id: string;
  quote_number: string;
  quote_type: string;
  requested_date_time: string | null;
  status: string;
  total_amount: number | null;
  valid_until: string;
  broadcast_status?: string | null;
  responded_at?: string | null;
  assigned_business_id?: string | null;
  assigned_business_name?: string | null;
}

export default function BusinessDetail() {
  const { id } = useParams<{ id: string }>();
  const { getBusinessById, updateBusiness } = useBusinesses();
  const { getToken } = useAuth();

  const [business, setBusiness] = useState<Business | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [isEditingPostals, setIsEditingPostals] = useState<boolean>(false);
  const [newPostalCode, setNewPostalCode] = useState<string>('');
  const [servicePostalCodes, setServicePostalCodes] = useState<string[]>([]);
  const [saving, setSaving] = useState<boolean>(false);
  const [jobs, setJobs] = useState<JobWithRelations[]>([]);
  const [loadingJobs, setLoadingJobs] = useState<boolean>(false);
  const [orgMembers, setOrgMembers] = useState<ClerkOrganizationMember[]>([]);
  const [loadingOrgMembers, setLoadingOrgMembers] = useState<boolean>(false);
  const [subscription, setSubscription] = useState<ClerkSubscription | null>(null);
  const [loadingSubscription, setLoadingSubscription] = useState<boolean>(false);
  const [quotes, setQuotes] = useState<BroadcastedQuote[]>([]);
  const [loadingQuotes, setLoadingQuotes] = useState<boolean>(false);

  useEffect(() => {
    const fetchBusiness = async () => {
      if (!id) {
        setLoading(false);
        return; // Don't fetch if ID is not available yet
      }
      setLoading(true);
      setError(null);
      try {
        const fetchedBusiness = await getBusinessById(id);
        setBusiness(fetchedBusiness);
        setServicePostalCodes(fetchedBusiness?.servicePostalCodes || []);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'An unknown error occurred');
      } finally {
        setLoading(false);
      }
    };

    fetchBusiness();
  }, [id, getBusinessById]);

  // Fetch quotes broadcast to this business
  useEffect(() => {
    const fetchQuotes = async () => {
      if (!business?.clerkOrganizationId) return;
      
      setLoadingQuotes(true);
      try {
        const { data, error } = await supabase
          .rpc('get_business_quotes', {
            p_clerk_org_id: business.clerkOrganizationId
          });
        
        if (error) throw error;
        
        // Transform data to match BroadcastedQuote interface
        const transformedQuotes: BroadcastedQuote[] = (data || []).map((quote: QuoteFromRPC) => ({
          id: quote.id,
          quote_number: quote.quote_number,
          requested_date_time: quote.requested_date_time || null,
          quote_type: quote.quote_type as 'exclusive' | 'broadcast',
          broadcast_status: quote.broadcast_status || null,
          status: quote.status,
          business_id: quote.business_id || null,
          total_amount: quote.total_amount || null,
          valid_until: quote.valid_until || null,
          responded_at: quote.responded_at || null,
          assigned_business_id: quote.assigned_business_id || null,
          assigned_business_name: quote.assigned_business_name || null,
        }));
        
        setQuotes(transformedQuotes);
      } catch (err) {
        console.error('Error fetching quotes:', err);
      } finally {
        setLoadingQuotes(false);
      }
    };

    fetchQuotes();
  }, [business?.clerkOrganizationId]);

  // Fetch jobs for this business
  useEffect(() => {
    const fetchJobs = async () => {
      if (!id) return;
      
      setLoadingJobs(true);
      try {
        const { data, error } = await supabase
          .from('jobs')
          .select(`
            *,
            customers:customer_id (
              *
            ),
            services:service_id (
              *
            ),
            properties:property_id (
              *
            )
          `)
          .eq('business_id', id)
          .order('date_time', { ascending: false });

        if (error) throw error;

        // Transform to JobWithRelations type
        const transformedJobs: JobWithRelations[] = (data || []).map((row) => ({
          id: row.id,
          customerId: row.customer_id,
          serviceId: row.service_id,
          businessId: row.business_id,
          propertyId: row.property_id,
          date_time: row.date_time,
          duration: row.duration,
          status: (row.status || 'scheduled') as JobStatus,
          price: row.price,
          notes: row.notes || '',
          isRecurring: row.is_recurring || false,
          recurringFrequency: (row.recurring_frequency as 'weekly' | 'bi-weekly' | 'tri-weekly' | 'monthly' | undefined) || undefined,
          recurringEndDate: row.recurring_end_date || undefined,
          recurringParentId: row.recurring_parent_id || undefined,
          createdAt: row.created_at || new Date().toISOString(),
          updatedAt: row.updated_at || new Date().toISOString(),
          service: row.services ? {
            id: row.service_id,
            name: row.services.name || 'Unknown Service',
            description: row.services.description || '',
            basePrice: row.services.base_price || 0,
            duration: row.services.duration || 60,
            category: (row.services.category || []) as ServiceCategory,
            addOns: (row.services.add_ons || []) as string[],
            createdAt: row.services.created_at || new Date().toISOString(),
            updatedAt: row.services.updated_at || new Date().toISOString()
          } : undefined,
          customer: row.customers ? {
            id: row.customer_id,
            name: row.customers.name || 'Unknown',
            email: row.customers.email || '',
            phone: row.customers.phone || '',
            createdAt: row.customers.created_at || new Date().toISOString(),
            updatedAt: row.customers.updated_at || new Date().toISOString()
          } : undefined,
          property: row.properties ? {
            id: row.property_id,
            customerId: row.properties.customer_id,
            name: row.properties.name || '',
            propertyType: row.properties.property_type as PropertyType,
            addressLine1: row.properties.address_line_1 || '',
            addressLine2: row.properties.address_line_2 || '',
            city: row.properties.city || '',
            state: row.properties.state || '',
            postalCode: row.properties.postal_code || '',
            squareFootage: row.properties.square_footage || 0,
            bedrooms: row.properties.bedrooms || 0,
            bathrooms: row.properties.bathrooms || 0,
            floors: row.properties.floors || 1,
            accessInstructions: row.properties.access_instructions || '',
            specialInstructions: row.properties.special_instructions || '',
            hasPets: row.properties.has_pets || false,
            petDetails: row.properties.pet_details || '',
            preferredCleaningDay: row.properties.preferred_cleaning_day as ('monday' | 'tuesday' | 'wednesday' | 'thursday' | 'friday' | 'saturday' | 'sunday') | undefined,
            requestedTimeSlot: (row.properties.requested_time_slot as 'morning' | 'midday' | 'afternoon' | null) || undefined,
            isActive: row.properties.is_active !== false,
            createdAt: row.properties.created_at || new Date().toISOString(),
            updatedAt: row.properties.updated_at || new Date().toISOString()
          } : undefined
        }));

        setJobs(transformedJobs);
      } catch (err) {
        console.error('Error fetching jobs:', err);
      } finally {
        setLoadingJobs(false);
      }
    };

    fetchJobs();
  }, [id, business?.businessName]);

  // Fetch organization members from Clerk via Edge Function
  useEffect(() => {
    const fetchOrgMembers = async () => {
      if (!business?.clerkOrganizationId) return;
      
      setLoadingOrgMembers(true);
      try {
        console.log('Fetching org members for:', business.clerkOrganizationId);
        const { data, error } = await supabase.functions.invoke('get-clerk-org-members', {
          body: { organizationId: business.clerkOrganizationId }
        });

        console.log('Org members response:', { data, error });

        if (error) throw error;

        setOrgMembers(data?.data || []);
      } catch (err) {
        console.error('Error fetching organization members:', err);
        setOrgMembers([]);
      } finally {
        setLoadingOrgMembers(false);
      }
    };

    fetchOrgMembers();
  }, [business?.clerkOrganizationId]);

  // Fetch subscription data from Clerk via Edge Function
  useEffect(() => {
    const fetchSubscription = async () => {
      if (!business?.clerkOrganizationId) return;
      
      setLoadingSubscription(true);
      try {
        const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
        const token = await getToken();
        
        if (!token) {
          console.warn('⚠️ No Clerk session token available');
          setSubscription(null);
          setLoadingSubscription(false);
          return;
        }
        
        const response = await fetch(
          `${supabaseUrl}/functions/v1/clerk-webhook?organizationId=${business.clerkOrganizationId}`,
          {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`,
            },
          }
        );

        if (!response.ok) {
          if (response.status === 503) {
            console.warn('⚠️ Subscription service unavailable (edge function not running)');
          } else {
            console.error('Failed to fetch subscription data:', response.status, response.statusText);
          }
          setSubscription(null);
          return;
        }

        const data = await response.json();
        setSubscription(data.subscription);
        console.log('✅ Subscription loaded:', data.subscription);
      } catch (err) {
        console.error('Error loading subscription:', err);
        setSubscription(null);
      } finally {
        setLoadingSubscription(false);
      }
    };

    fetchSubscription();
  }, [business?.clerkOrganizationId]);

  if (loading) {
    return <div className="flex justify-center items-center h-64"><p>Loading business details...</p></div>;
  }

  if (error) {
    return <div className="text-red-500 text-center p-4">Error loading business: {error}</div>;
  }

  if (!business) {
    return (
      <div className="text-center p-8">
        <Building2 className="h-12 w-12 text-gray-300 mx-auto mb-4" />
        <h2 className="text-2xl font-semibold text-gray-700">Business Not Found</h2>
        <p className="text-gray-500 mt-2">We couldn't find a business with that ID.</p>
        <Link to="/businesses" className="mt-6 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500">
          <ArrowLeft className="h-5 w-5 mr-2" />
          Return to Businesses List
        </Link>
      </div>
    );
  }

  const handleAddPostalCode = () => {
    const cleanPostalCode = newPostalCode.trim().replaceAll(/\D/g, '');
    if (cleanPostalCode.length === 5 && !servicePostalCodes.includes(cleanPostalCode)) {
      setServicePostalCodes([...servicePostalCodes, cleanPostalCode]);
      setNewPostalCode('');
    }
  };

  const handleRemovePostalCode = (postalCode: string) => {
    setServicePostalCodes(servicePostalCodes.filter(postal => postal !== postalCode));
  };

  const handleSavePostalCodes = async () => {
    if (!business || !id) return;

    try {
      setSaving(true);
      setError(null);
      
      // Ensure servicePostalCodes is properly formatted as an array
      const updateData = { 
        servicePostalCodes: servicePostalCodes.length > 0 ? servicePostalCodes : [] 
      };
      
      console.log('Updating business with Postal codes:', updateData);
      console.log('Number of Postal codes:', servicePostalCodes.length);
      
      await updateBusiness(id, updateData);
      setBusiness({ ...business, servicePostalCodes });
      setIsEditingPostals(false);
    } catch (err) {
      console.error('Error saving Postal codes:', err);
      setError(err instanceof Error ? err.message : 'Failed to update service areas');
    } finally {
      setSaving(false);
    }
  };

  const handleCancelEditPostalss = () => {
    setServicePostalCodes(business?.servicePostalCodes || []);
    setNewPostalCode('');
    setIsEditingPostals(false);
  };

  const handlePostalCodeKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddPostalCode();
    }
  };

  return (
    <div className="space-y-4 sm:space-y-6 pb-6">
      <div className="px-4 sm:px-0">
        <Link to="/service-providers" className="inline-flex items-center text-sm font-medium text-indigo-600 hover:text-indigo-800 active:text-indigo-900 py-2">
          <ArrowLeft className="h-4 w-4 sm:h-5 sm:w-5 mr-2" />
          <span>Back to Businesses</span>
        </Link>
      </div>

      {/* Business Overview */}
      <div className="bg-white p-4 sm:p-6 rounded-none sm:rounded-xl shadow-sm border-y sm:border border-gray-200">
        <div className="flex items-start justify-between gap-3 mb-6">
          <div className="flex-1 min-w-0">
            <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900 break-words">{business.businessName}</h1>
          </div>
          <span className={`px-2.5 sm:px-3 py-1 rounded-full text-xs sm:text-sm font-medium whitespace-nowrap flex-shrink-0 ${
            business.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
          }`}>
            {business.status.charAt(0).toUpperCase() + business.status.slice(1)}
          </span>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {/* Contact Info */}
          <div className="p-4 bg-gray-50 rounded-lg border border-gray-100">
            <h3 className="text-sm font-semibold text-gray-800 mb-3">Contact Information</h3>
            <div className="space-y-3">
              {business.ownerName && (
                <div className="flex items-start text-gray-700">
                  <User className="h-4 w-4 mr-2.5 text-gray-400 flex-shrink-0 mt-0.5" />
                  <span className="text-sm break-words">Owned by {business.ownerName}</span>
                </div>
              )}
              {business.email && (
                <div className="flex items-start text-gray-700">
                  <Mail className="h-4 w-4 mr-2.5 text-gray-400 flex-shrink-0 mt-0.5" />
                  <span className="text-sm break-all">{business.email}</span>
                </div>
              )}
              {business.phone && (
                <div className="flex items-start text-gray-700">
                  <Phone className="h-4 w-4 mr-2.5 text-gray-400 flex-shrink-0 mt-0.5" />
                  <a href={`tel:${business.phone}`} className="text-sm text-indigo-600 hover:text-indigo-800 active:text-indigo-900">{business.phone}</a>
                </div>
              )}
              {business.address && (business.address.addressLine1 || business.address.addressCity) && (
                <div className="flex items-start text-gray-700">
                  <MapPin className="h-4 w-4 mr-2.5 text-gray-400 flex-shrink-0 mt-0.5" />
                  <div className="text-sm">
                    {business.address.addressLine1 && <>{business.address.addressLine1}<br /></>}
                    {business.address.addressLine2 && <>{business.address.addressLine2}<br /></>}
                    {business.address.addressCity && business.address.addressState && business.address.addressPostalCode && (
                      <>{business.address.addressCity}, {business.address.addressState} {business.address.addressPostalCode}</>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Business Details */}
          <div className="p-4 bg-gray-50 rounded-lg border border-gray-100 col-span-1 sm:col-span-2">
            <h3 className="text-sm font-semibold text-gray-800 mb-3">Business Details</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="flex items-start">
                <Shield className="h-4 w-4 mr-2.5 text-gray-400 flex-shrink-0 mt-0.5" />
                <div className="min-w-0 flex-1">
                  <p className="text-xs text-gray-500 mb-1">Clerk Org ID</p>
                  <p className="font-medium text-gray-800 font-mono text-xs break-all">{business.clerkOrganizationId || 'Not set'}</p>
                </div>
              </div>
              <div className="flex items-start">
                <Calendar className="h-4 w-4 mr-2.5 text-gray-400 flex-shrink-0 mt-0.5" />
                <div className="min-w-0 flex-1">
                  <p className="text-xs text-gray-500 mb-1">Created</p>
                  <p className="font-medium text-gray-800 text-sm">
                    {business.createdAt ? DateUtils.formatDateLong(business.createdAt) : 'N/A'}
                  </p>
                  <div className="text-xs text-gray-500">
                    {business.createdAt ? new Date(business.createdAt).toLocaleTimeString('en-US', {
                      hour: '2-digit',
                      minute: '2-digit'
                    }) : ''}
                  </div>
                </div>
              </div>
              {business.insuranceInfo && (
                <div className="flex items-start col-span-2">
                  <Shield className="h-4 w-4 mr-2.5 text-gray-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-xs text-gray-500 mb-1">Insurance</p>
                    <p className="font-medium text-gray-800 text-sm">{business.insuranceInfo.provider} - Exp: {DateUtils.formatDateLong(business.insuranceInfo.expirationDate)}</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6">
        {/* Landing Page Access */}
        <div className="bg-white p-4 sm:p-6 rounded-none sm:rounded-xl shadow-sm border-y sm:border border-gray-200 col-span-1 md:col-span-3">
          <h2 className="text-base sm:text-lg font-semibold text-gray-800 mb-3 sm:mb-4 flex items-center">
            <Globe className="h-4 w-4 sm:h-5 sm:w-5 mr-2 text-gray-400" />
            Landing Page Access
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {/* Website Features Status */}
            <div className="p-4 bg-gray-50 rounded-lg border border-gray-100">
              <p className="text-xs text-gray-500 mb-2">Website Features</p>
              <div className="flex items-center gap-2">
                {business.hasWebsiteFeatures ? (
                  <>
                    <CheckCircle2 className="h-5 w-5 text-green-600" />
                    <span className="text-sm font-semibold text-green-700">Enabled</span>
                  </>
                ) : (
                  <>
                    <XCircle className="h-5 w-5 text-red-600" />
                    <span className="text-sm font-semibold text-red-700">Disabled</span>
                  </>
                )}
              </div>
              <p className="text-xs text-gray-500 mt-1">
                {business.hasWebsiteFeatures 
                  ? 'Can publish landing page'
                  : 'Cannot publish landing page'}
              </p>
            </div>

            {/* Access Reason */}
            <div className="p-4 bg-gray-50 rounded-lg border border-gray-100">
              <p className="text-xs text-gray-500 mb-2">Access Reason</p>
              {business.freeWebsiteOverride ? (
                <div className="space-y-1">
                  <div className="flex items-center gap-1.5">
                    <Shield className="h-4 w-4 text-blue-600" />
                    <span className="text-sm font-medium text-blue-700">Manual Override</span>
                  </div>
                  <p className="text-xs text-gray-600">Free website granted by admin</p>
                </div>
              ) : business.subscriptionPlan ? (
                <div className="space-y-1">
                  <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-indigo-100 text-indigo-800">
                    {business.subscriptionPlan}
                  </span>
                  <p className="text-xs text-gray-600">
                    {business.hasWebsiteFeatures 
                      ? 'Plan includes website features' 
                      : 'Plan does not include website'}
                  </p>
                </div>
              ) : (
                <div className="space-y-1">
                  <span className="text-sm text-gray-600">No subscription</span>
                  <p className="text-xs text-gray-500">Free plan (no website access)</p>
                </div>
              )}
            </div>

            {/* Landing Page Status */}
            <div className="p-4 bg-gray-50 rounded-lg border border-gray-100">
              <p className="text-xs text-gray-500 mb-2">Page Status</p>
              <span className={`inline-flex px-3 py-1 rounded-full text-sm font-medium ${
                business.landingPageStatus === 'active'
                  ? 'bg-green-100 text-green-800'
                  : business.landingPageStatus === 'pending'
                  ? 'bg-yellow-100 text-yellow-800'
                  : 'bg-gray-100 text-gray-800'
              }`}>
                {business.landingPageStatus 
                  ? business.landingPageStatus.charAt(0).toUpperCase() + business.landingPageStatus.slice(1)
                  : 'Not Set'}
              </span>
              <p className="text-xs text-gray-500 mt-2">
                {business.landingPageStatus === 'active' 
                  ? 'Publicly visible'
                  : business.landingPageStatus === 'pending'
                  ? 'Awaiting activation'
                  : 'Not published'}
              </p>
            </div>

            {/* Landing Page URL */}
            {business.slug && (
              <div className="p-4 bg-gray-50 rounded-lg border border-gray-100 col-span-1 sm:col-span-2 lg:col-span-3">
                <p className="text-xs text-gray-500 mb-2">Landing Page URL</p>
                <div className="flex items-center gap-2 flex-wrap">
                  <code className="text-sm bg-white px-3 py-1.5 rounded border border-gray-200 font-mono">
                    /service-providers/{business.slug}
                  </code>
                  <a
                    href={`${import.meta.env.VITE_MARKETING_URL || 'https://www.gositback.com'}/service-providers/${business.slug}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-indigo-600 hover:text-indigo-800 hover:bg-indigo-50 rounded transition"
                  >
                    <ExternalLink className="h-3.5 w-3.5" />
                    View Page
                  </a>
                </div>
                {business.landingPageStatus !== 'active' && (
                  <div className="mt-2 flex items-start gap-2 p-2 bg-yellow-50 border border-yellow-200 rounded">
                    <AlertCircle className="h-4 w-4 text-yellow-600 flex-shrink-0 mt-0.5" />
                    <p className="text-xs text-yellow-800">
                      Page exists but is not publicly visible (status: {business.landingPageStatus || 'inactive'})
                    </p>
                  </div>
                )}
                {business.landingPageStatus === 'active' && !business.hasWebsiteFeatures && (
                  <div className="mt-2 flex items-start gap-2 p-2 bg-red-50 border border-red-200 rounded">
                    <AlertCircle className="h-4 w-4 text-red-600 flex-shrink-0 mt-0.5" />
                    <p className="text-xs text-red-800">
                      Warning: Page is active but organization lacks website features. Public access will be denied.
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* No Landing Page */}
            {!business.slug && (
              <div className="p-4 bg-gray-50 rounded-lg border border-gray-100 col-span-1 sm:col-span-2 lg:col-span-3">
                <div className="flex items-center gap-2 text-gray-500">
                  <AlertCircle className="h-4 w-4" />
                  <span className="text-sm">No landing page created yet</span>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Business owner needs to set up their landing page from the Partner Portal
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Organization Members */}
        {business.clerkOrganizationId && (
          <div className="bg-white p-4 sm:p-6 rounded-none sm:rounded-xl shadow-sm border-y sm:border border-gray-200 col-span-1 md:col-span-3">
            <div className="flex items-center justify-between mb-3 sm:mb-4">
              <h2 className="text-base sm:text-lg font-semibold text-gray-800 flex items-center">
                <Users className="h-4 w-4 sm:h-5 sm:w-5 mr-2 text-gray-400" />
                <span>Organization Members</span>
              </h2>
            </div>
            <p className="text-xs sm:text-sm text-gray-600 mb-3 sm:mb-4">
              Members with access to this business via Clerk organization.
            </p>

            {loadingOrgMembers && (
              <div className="flex justify-center items-center py-4">
                <p className="text-gray-500 text-sm">Loading members...</p>
              </div>
            )}
            
            {!loadingOrgMembers && orgMembers.length === 0 && (
              <div className="text-center py-4">
                <Users className="h-8 w-8 text-gray-300 mx-auto mb-2" />
                <p className="text-gray-500 text-sm">No members found</p>
                <p className="text-gray-400 text-xs mt-1">Organization may not have any members yet</p>
              </div>
            )}
            
            {!loadingOrgMembers && orgMembers.length > 0 && (
              <div className="space-y-2">
                {orgMembers.map((member: ClerkOrganizationMember) => {
                  const userData = member.public_user_data;
                  const hasImage = userData?.has_image;
                  const imageUrl = userData?.image_url || userData?.profile_image_url;
                  
                  return (
                    <div key={member.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-100">
                      <div className="flex items-center gap-3">
                        {hasImage && imageUrl ? (
                          <img 
                            src={imageUrl} 
                            alt={`${userData?.first_name} ${userData?.last_name}`}
                            className="h-10 w-10 rounded-full object-cover"
                          />
                        ) : (
                          <div className="h-10 w-10 rounded-full bg-indigo-100 flex items-center justify-center">
                            <User className="h-5 w-5 text-indigo-600" />
                          </div>
                        )}
                        <div>
                          <div className="flex items-center gap-2">
                            <p className="font-medium text-gray-900">
                              {userData?.first_name} {userData?.last_name}
                            </p>
                            {member.role === 'org:admin' && (
                              <span className="px-2 py-0.5 text-xs font-medium rounded bg-purple-100 text-purple-700">
                                Admin
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-gray-500">{userData?.identifier}</p>
                          {userData?.last_sign_in_at && (
                            <p className="text-xs text-gray-400 mt-0.5">
                              Last active: {new Date(userData.last_sign_in_at).toLocaleDateString('en-US', {
                                month: 'short',
                                day: 'numeric',
                                year: 'numeric'
                              })}
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {userData?.two_factor_enabled && (
                          <span className="px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-700" title="Two-factor authentication enabled">
                            2FA
                          </span>
                        )}
                        {userData?.banned && (
                          <span className="px-2 py-1 text-xs font-medium rounded-full bg-red-100 text-red-700">
                            Banned
                          </span>
                        )}
                        {userData?.locked && (
                          <span className="px-2 py-1 text-xs font-medium rounded-full bg-orange-100 text-orange-700">
                            Locked
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* Organization Subscription */}
        {business.clerkOrganizationId && (
          <div className="bg-white p-4 sm:p-6 rounded-none sm:rounded-xl shadow-sm border-y sm:border border-gray-200 col-span-1 md:col-span-3">
            <div className="flex items-center justify-between mb-3 sm:mb-4">
              <h2 className="text-base sm:text-lg font-semibold text-gray-800 flex items-center">
                <CreditCard className="h-4 w-4 sm:h-5 sm:w-5 mr-2 text-gray-400" />
                <span>Subscription</span>
              </h2>
            </div>
            <p className="text-xs sm:text-sm text-gray-600 mb-3 sm:mb-4">
              Active subscription plan for this organization from Clerk.
            </p>

            {loadingSubscription && (
              <div className="flex justify-center items-center py-4">
                <p className="text-gray-500 text-sm">Loading subscription...</p>
              </div>
            )}
            
            {!loadingSubscription && !subscription && (
              <div className="text-center py-4">
                <CreditCard className="h-8 w-8 text-gray-300 mx-auto mb-2" />
                <p className="text-gray-500 text-sm">No active subscription</p>
                <p className="text-gray-400 text-xs mt-1">Organization is on the free plan</p>
              </div>
            )}
            
            {!loadingSubscription && subscription && (
              <div className="space-y-4">

                {subscription.createdAt && (
                  <div className="p-3 bg-gray-50 border border-gray-100 rounded-lg">
                    <p className="text-xs text-gray-500 mb-0.5">Created on</p>
                    <p className="text-sm font-medium text-gray-900">
                      {new Date(subscription.createdAt).toLocaleDateString('en-US', {
                        month: 'long',
                        day: 'numeric',
                        year: 'numeric'
                      })}
                    </p>
                  </div>
                )}

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="p-4 bg-gray-50 rounded-lg border border-gray-100">
                    <p className="text-xs text-gray-500 mb-1">Plan</p>
                    <div className="flex items-center gap-2">
                      <p className="text-lg font-semibold text-gray-900">
                        {subscription.plan?.name || 'Free'}
                      </p>
                      {subscription.isFreeTrial && (
                        <span className="px-2 py-0.5 text-xs font-medium rounded bg-blue-100 text-blue-700">
                          Free Trial
                        </span>
                      )}
                      {subscription.plan?.price && (
                        <span className="text-sm text-gray-600">
                          ${(subscription.plan.price / 100).toFixed(2)} /{subscription.plan.period === 'year' ? 'year' : 'month'}
                        </span>
                      )}
                    </div>
                    {subscription.plan?.period && (
                      <p className="text-xs text-gray-500 mt-1">
                        Billed {subscription.plan.period === 'year' ? 'annually' : 'monthly'}
                      </p>
                    )}
                  </div>
                  
                  <div className="p-4 bg-gray-50 rounded-lg border border-gray-100">
                    <p className="text-xs text-gray-500 mb-1">Subscription Status</p>
                    <span className={`inline-flex px-3 py-1 rounded-full text-sm font-medium ${
                      subscription.status === 'active'
                        ? 'bg-green-100 text-green-800'
                        : 'bg-gray-100 text-gray-800'
                    }`}>
                      {subscription.status.charAt(0).toUpperCase() + subscription.status.slice(1)}
                    </span>
                  </div>

                  <div className="p-4 bg-gray-50 rounded-lg border border-gray-100">
                    <p className="text-xs text-gray-500 mb-1">Landing Page Status</p>
                    <span className={`inline-flex px-3 py-1 rounded-full text-sm font-medium ${
                      business.landingPageStatus === 'active'
                        ? 'bg-green-100 text-green-800'
                        : business.landingPageStatus === 'pending'
                        ? 'bg-yellow-100 text-yellow-800'
                        : 'bg-gray-100 text-gray-800'
                    }`}>
                      {business.landingPageStatus 
                        ? business.landingPageStatus.charAt(0).toUpperCase() + business.landingPageStatus.slice(1)
                        : 'Not Set'
                      }
                    </span>
                    <p className="text-xs text-gray-500 mt-1">
                      {business.landingPageStatus === 'active' ? 'Publicly listed' : 'Not publicly listed'}
                    </p>
                    {business.landingPageStatus === 'active' && business.slug && (
                      <a
                        href={`${import.meta.env.VITE_CUSTOMER_APP_URL || 'https://www.gositback.com'}/service-providers/${business.slug}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-xs text-indigo-600 hover:text-indigo-800 mt-2 font-medium"
                      >
                        View Landing Page
                        <ExternalLink className="h-3 w-3" />
                      </a>
                    )}
                  </div>

                  <div className="p-4 bg-gray-50 rounded-lg border border-gray-100">
                    <p className="text-xs text-gray-500 mb-1">Platform Status</p>
                    <span className={`inline-flex px-3 py-1 rounded-full text-sm font-medium ${
                      business.status === 'active'
                        ? 'bg-green-100 text-green-800'
                        : business.status === 'pending'
                        ? 'bg-yellow-100 text-yellow-800'
                        : 'bg-gray-100 text-gray-800'
                    }`}>
                      {business.status.charAt(0).toUpperCase() + business.status.slice(1)}
                    </span>
                    <p className="text-xs text-gray-500 mt-1">
                      Service provider account
                    </p>
                  </div>
                  
                  {subscription.nextPayment && (
                    <>
                      <div className="p-4 bg-gray-50 rounded-lg border border-gray-100">
                        <p className="text-xs text-gray-500 mb-1">Next Payment</p>
                        <p className="text-lg font-semibold text-gray-900">
                          {new Intl.NumberFormat('en-US', {
                            style: 'currency',
                            currency: subscription.nextPayment.currency.toUpperCase(),
                          }).format(subscription.nextPayment.amount / 100)}
                        </p>
                        <p className="text-xs text-gray-500 mt-1">
                          Due {new Date(subscription.nextPayment.date * 1000).toLocaleDateString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric'
                          })}
                        </p>
                      </div>
                      
                      <div className="p-4 bg-gray-50 rounded-lg border border-gray-100">
                        <p className="text-xs text-gray-500 mb-1">Period Ends</p>
                        <p className="text-base font-medium text-gray-900">
                          {subscription.periodEnd ? new Date(subscription.periodEnd * 1000).toLocaleDateString('en-US', {
                            month: 'long',
                            day: 'numeric',
                            year: 'numeric'
                          }) : 'N/A'}
                        </p>
                      </div>
                    </>
                  )}
                  
                  {(subscription.billingCycle || subscription.plan?.period) && (
                    <div className="p-4 bg-gray-50 rounded-lg border border-gray-100">
                      <p className="text-xs text-gray-500 mb-1">Billing Cycle</p>
                      <p className="text-base font-medium text-gray-900">
                        {subscription.billingCycle === 'annual' || subscription.plan?.period === 'year' ? 'Annual' : 'Monthly'}
                      </p>
                    </div>
                  )}
                  
                  {subscription.paymentMethod && (
                    <div className="p-4 bg-gray-50 rounded-lg border border-gray-100">
                      <p className="text-xs text-gray-500 mb-1">Payment Method</p>
                      <p className="text-base font-medium text-gray-900">
                        {subscription.paymentMethod.type.charAt(0).toUpperCase() + subscription.paymentMethod.type.slice(1)} •• {subscription.paymentMethod.last4}
                      </p>
                    </div>
                  )}
                  
                  <div className="p-4 bg-gray-50 rounded-lg border border-gray-100">
                    <p className="text-xs text-gray-500 mb-1">Lifetime Paid</p>
                    <p className="text-lg font-semibold text-gray-900">
                      {subscription.lifetimePaid !== undefined ? new Intl.NumberFormat('en-US', {
                        style: 'currency',
                        currency: subscription.nextPayment?.currency.toUpperCase() || 'USD',
                      }).format(subscription.lifetimePaid / 100) : '$0.00'}
                    </p>
                  </div>
                </div>
                
                {subscription.isFreeTrial && subscription.periodEnd && (
                  <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                    <p className="text-sm text-blue-800">
                      <span className="font-medium">Free trial</span> - Ends on {new Date(subscription.periodEnd * 1000).toLocaleDateString('en-US', {
                        month: 'long',
                        day: 'numeric',
                        year: 'numeric'
                      })}
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Service Areas Section */}
        <div className="bg-white p-4 sm:p-6 rounded-none sm:rounded-xl shadow-sm border-y sm:border border-gray-200 col-span-1 md:col-span-3">
          <div className="flex items-start sm:items-center justify-between mb-3 sm:mb-4 gap-3">
            <h2 className="text-base sm:text-lg font-semibold text-gray-800 flex items-center flex-1 min-w-0">
              <MapPin className="h-4 w-4 sm:h-5 sm:w-5 mr-2 text-gray-400 flex-shrink-0" />
              <span>Service Areas</span>
            </h2>
            {isEditingPostals ? (
              <div className="flex gap-2 flex-shrink-0">
                <button
                  onClick={handleCancelEditPostalss}
                  disabled={saving}
                  className="flex items-center px-2.5 sm:px-3 py-1.5 sm:py-1 text-xs sm:text-sm bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 active:bg-gray-400 transition disabled:opacity-50 min-h-[36px] sm:min-h-0"
                >
                  <X className="h-3.5 w-3.5 sm:h-4 sm:w-4 sm:mr-1" />
                  <span className="hidden sm:inline">Cancel</span>
                </button>
                <button
                  onClick={handleSavePostalCodes}
                  disabled={saving}
                  className="flex items-center px-2.5 sm:px-3 py-1.5 sm:py-1 text-xs sm:text-sm bg-indigo-600 text-white rounded-md hover:bg-indigo-700 active:bg-indigo-800 transition disabled:opacity-50 min-h-[36px] sm:min-h-0"
                >
                  <Save className="h-3.5 w-3.5 sm:h-4 sm:w-4 sm:mr-1" />
                  <span className="hidden sm:inline">{saving ? 'Saving...' : 'Save'}</span>
                  <span className="sm:hidden">{saving ? '...' : ''}</span>
                </button>
              </div>
            ) : (
              <button
                onClick={() => setIsEditingPostals(true)}
                className="flex items-center px-2.5 sm:px-3 py-1.5 sm:py-1 text-xs sm:text-sm bg-indigo-600 text-white rounded-md hover:bg-indigo-700 active:bg-indigo-800 transition min-h-[36px] sm:min-h-0 flex-shrink-0"
              >
                <Edit className="h-3.5 w-3.5 sm:h-4 sm:w-4 sm:mr-1" />
                <span className="hidden sm:inline">Edit</span>
              </button>
            )}
          </div>
          <p className="text-xs sm:text-sm text-gray-600 mb-3 sm:mb-4">
            Specify which Postal codes this business services. This is used for geo-targeted quote broadcasting.
          </p>

          {isEditingPostals && (
            <div className="mb-4">
              <label htmlFor="postalCodeInput" className="block text-xs sm:text-sm font-medium text-gray-700 mb-2">Add Postal Code</label>
              <div className="flex gap-2">
                <input
                  id="postalCodeInput"
                  type="text"
                  value={newPostalCode}
                  onChange={(e) => setNewPostalCode(e.target.value)}
                  onKeyDown={handlePostalCodeKeyDown}
                  placeholder="Enter 5-digit code"
                  maxLength={5}
                  className="flex-1 px-3 sm:px-4 py-2.5 sm:py-2 text-base border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 min-h-[44px]"
                />
                <button
                  type="button"
                  onClick={handleAddPostalCode}
                  className="px-3 sm:px-4 py-2.5 sm:py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 active:bg-indigo-800 disabled:opacity-50 transition text-sm sm:text-base font-medium min-h-[44px] flex items-center justify-center"
                  disabled={newPostalCode.replaceAll(/\D/g, '').length !== 5}
                >
                  <Plus className="h-4 w-4 sm:inline sm:mr-1" />
                  <span className="hidden sm:inline">Add</span>
                </button>
              </div>
            </div>
          )}

          <div className="flex flex-wrap gap-2">
            {servicePostalCodes.length === 0 ? (
              <p className="text-gray-500 text-xs sm:text-sm">No service areas configured</p>
            ) : (
              servicePostalCodes.map((postalCode) => (
                <span
                  key={postalCode}
                  className="inline-flex items-center gap-1 px-2.5 sm:px-3 py-1.5 sm:py-1 bg-blue-100 text-blue-800 rounded-full text-xs sm:text-sm font-medium"
                >
                  {postalCode}
                  {isEditingPostals && (
                    <button
                      type="button"
                      onClick={() => handleRemovePostalCode(postalCode)}
                      className="ml-1 text-blue-600 hover:text-blue-800 active:text-blue-900 p-0.5 -mr-1"
                      aria-label={`Remove ${postalCode}`}
                    >
                      <X className="h-3.5 w-3.5 sm:h-3 sm:w-3" />
                    </button>
                  )}
                </span>
              ))
            )}
          </div>
        </div>

        {/* Business Services Section */}
        <div className="col-span-1 md:col-span-3">
          <BusinessServicesSection businessId={id || ''} />
        </div>

        {/* Jobs Section */}
        <div className="bg-white p-4 sm:p-6 rounded-none sm:rounded-xl shadow-sm border-y sm:border border-gray-200 col-span-1 md:col-span-3">
          <div className="flex items-center justify-between mb-3 sm:mb-4">
            <h2 className="text-base sm:text-lg font-semibold text-gray-800 flex items-center">
              <JobIcon className="h-4 w-4 sm:h-5 sm:w-5 mr-2 text-gray-400" />
              <span>Assigned Jobs ({jobs.length})</span>
            </h2>
          </div>
          <p className="text-xs sm:text-sm text-gray-600 mb-3 sm:mb-4">
            All jobs assigned to this business.
          </p>

          {loadingJobs && (
            <div className="flex justify-center items-center py-8">
              <p className="text-gray-500">Loading jobs...</p>
            </div>
          )}
          
          {!loadingJobs && jobs.length === 0 && (
            <div className="text-center py-8">
              <JobIcon className="h-12 w-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500">No jobs assigned to this business yet.</p>
            </div>
          )}
          
          {!loadingJobs && jobs.length > 0 && (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Date
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Customer
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Service
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Address
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Price
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {jobs.map((job) => (
                    <tr key={job.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                        {DateUtils.formatDateLong(job.date_time)}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-900">
                        <div>
                          <div className="font-medium">{job.customer?.name || 'Unknown'}</div>
                          <div className="text-gray-500 text-xs">{job.customer?.email || ''}</div>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-900">
                        {job.service?.name || 'Unknown Service'}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-500 max-w-xs truncate">
                        {job.property?.addressLine1 || 'No address'}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(job.status, 'job')}`}>
                          {getJobStatusLabel(job.status)}
                        </span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                        ${job.price.toFixed(2)}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm">
                        <Link
                          to={`/jobs/${job.id}`}
                          className="text-indigo-600 hover:text-indigo-900 flex items-center"
                        >
                          View
                          <ExternalLink className="h-3 w-3 ml-1" />
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Quotes Broadcasted To Service Provider */}
      <div className="bg-white p-4 sm:p-6 rounded-none sm:rounded-xl shadow-sm border-y sm:border border-gray-200 col-span-1 md:col-span-3">
        <div className="flex items-center justify-between mb-3 sm:mb-4">
          <h2 className="text-base sm:text-lg font-semibold text-gray-800 flex items-center">
            <Briefcase className="h-4 w-4 sm:h-5 sm:w-5 mr-2 text-gray-400" />
            <span>Broadcasted Quotes ({quotes.length})</span>
          </h2>
        </div>
        <p className="text-xs sm:text-sm text-gray-600 mb-3 sm:mb-4">
          Quotes that have been broadcast to this business (both general broadcasts and exclusive quotes).
        </p>

        {loadingQuotes && (
          <div className="flex justify-center items-center py-8">
            <p className="text-gray-500">Loading quotes...</p>
          </div>
        )}
        
        {!loadingQuotes && quotes.length === 0 && (
          <div className="text-center py-8">
            <Briefcase className="h-12 w-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500">No quotes broadcast to this business yet.</p>
          </div>
        )}
        
        {!loadingQuotes && quotes.length > 0 && (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Quote #
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Requested Date
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Type
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Provider Response
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Quote Status
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Amount
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Valid Until
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {quotes.map((quote) => {
                  // Determine provider's response to this quote
                  const getProviderResponse = () => {
                    if (!quote.broadcast_status) return { label: 'N/A', color: 'bg-gray-100 text-gray-800' };
                    
                    const status = quote.broadcast_status.toLowerCase();
                    
                    if (status === 'accepted') {
                      // Check if this business won the quote
                      if (quote.status === 'assigned' && quote.business_id === id) {
                        return { label: 'Won', color: 'bg-green-100 text-green-800', icon: CheckCircle2 };
                      }
                      return { label: 'Accepted', color: 'bg-blue-100 text-blue-800' };
                    }
                    
                    if (status === 'declined') {
                      return { label: 'Rejected', color: 'bg-red-100 text-red-800', icon: XCircle };
                    }
                    
                    if (status === 'expired') {
                      return { label: 'Expired', color: 'bg-gray-100 text-gray-800' };
                    }
                    
                    if (status === 'pending') {
                      // If quote is already assigned to someone else, this provider lost
                      if (quote.status === 'assigned' && quote.business_id !== id) {
                        return { label: 'Lost', color: 'bg-orange-100 text-orange-800', icon: AlertCircle };
                      }
                      return { label: 'Pending', color: 'bg-yellow-100 text-yellow-800' };
                    }
                    
                    return { label: status, color: 'bg-gray-100 text-gray-800' };
                  };
                  
                  const providerResponse = getProviderResponse();
                  const ResponseIcon = providerResponse.icon;
                  
                  return (
                    <tr key={quote.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
                        {quote.quote_number}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                        {quote.requested_date_time
                          ? new Date(quote.requested_date_time).toLocaleDateString('en-US', {
                              month: 'short',
                              day: 'numeric',
                              year: 'numeric',
                              hour: 'numeric',
                              minute: '2-digit'
                            })
                          : 'N/A'}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          quote.quote_type === 'exclusive'
                            ? 'bg-purple-100 text-purple-800'
                            : 'bg-blue-100 text-blue-800'
                        }`}>
                          {quote.quote_type === 'exclusive' ? 'Exclusive' : 'Broadcast'}
                        </span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <div className="flex flex-col gap-1">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${providerResponse.color}`}>
                            {ResponseIcon && <ResponseIcon className="h-3 w-3 mr-1" />}
                            {providerResponse.label}
                          </span>
                          {quote.responded_at && (
                            <span className="text-xs text-gray-500">
                              {new Date(quote.responded_at).toLocaleDateString('en-US', {
                                month: 'short',
                                day: 'numeric',
                                hour: 'numeric',
                                minute: '2-digit'
                              })}
                            </span>
                          )}
                          {providerResponse.label === 'Lost' && quote.assigned_business_id && quote.assigned_business_name && (
                            <Link
                              to={`/service-providers/${quote.assigned_business_id}`}
                              className="text-xs text-indigo-600 hover:text-indigo-800 active:text-indigo-900 inline-flex items-center"
                            >
                              Won by: {quote.assigned_business_name}
                              <ExternalLink className="h-2.5 w-2.5 ml-0.5" />
                            </Link>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          quote.status === 'assigned'
                            ? 'bg-green-100 text-green-800'
                            : quote.status === 'pending_assignment' || quote.status === 'exclusive_pending'
                            ? 'bg-yellow-100 text-yellow-800'
                            : 'bg-gray-100 text-gray-800'
                        }`}>
                          {quote.status.replace(/_/g, ' ').replace(/\b\w/g, (l: string) => l.toUpperCase())}
                        </span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                        ${quote.total_amount?.toFixed(2) || '0.00'}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                        {quote.valid_until
                          ? new Date(quote.valid_until).toLocaleDateString('en-US', {
                              month: 'short',
                              day: 'numeric',
                              year: 'numeric'
                            })
                          : 'N/A'}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm">
                        <Link
                          to={`/quotes/${quote.id}`}
                          className="text-indigo-600 hover:text-indigo-800 active:text-indigo-900 font-medium inline-flex items-center"
                        >
                          View
                          <ExternalLink className="h-3 w-3 ml-1" />
                        </Link>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}